import { Pencil } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiErrorMessage } from "@/lib/api/_client";
import { useUpdateDashboardCashThreshold } from "@/lib/hooks/use-dashboard-settings";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { T } from "./cash-threshold-editor.i18n";

type CashThresholdEditorProps = {
  threshold: number;
  currency: string;
};

/** Click-to-edit: pencil → type → Enter saves, Esc cancels. */
export function CashThresholdEditor({ threshold, currency }: CashThresholdEditorProps) {
  const t = useT(T);
  const { formatNumber, parseNumber } = useRegionalFormat();
  const update = useUpdateDashboardCashThreshold();
  const [draft, setDraft] = useState<string | null>(null);

  const formatted = formatNumber(threshold, { maximumFractionDigits: 0 });

  const save = () => {
    if (draft === null) return;
    const value = parseNumber(draft);
    if (value === null || value < 0) {
      toast.error(t.thresholdInvalid);
      return;
    }
    setDraft(null);
    if (value === threshold) return;
    update.mutate(value, { onError: (err) => toast.error(apiErrorMessage(err, t.saveFailed)) });
  };

  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-muted-foreground">{t.cashThreshold}</span>
      {draft === null ? (
        <>
          <span className="font-medium">
            {currency} {formatted}
          </span>
          <Button
            size="icon"
            variant="ghost"
            tooltip={t.editThreshold}
            onClick={() => setDraft(formatted)}
          >
            <Pencil />
          </Button>
        </>
      ) : (
        <div className="flex items-center gap-2">
          <span className="font-medium">{currency}</span>
          <Input
            autoFocus
            // Typing replaces the old amount rather than adding to it.
            onFocus={(e) => e.target.select()}
            inputMode="numeric"
            aria-label={t.cashThreshold}
            className="w-40"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") save();
              if (e.key === "Escape") setDraft(null);
            }}
            onBlur={() => setDraft(null)}
          />
        </div>
      )}
    </div>
  );
}
